import React, { useState, useEffect } from "react";
import { PriceFormModal, formatPriceRange } from "../../user/Ads/components/FilterModal/PriceFormModal";
import { DateFormModal, formatDateRange } from "../../user/Ads/components/FilterModal/DateFormModal";
import { SelectFormModal } from "./SelectFormModal";
import { FilterKey, FilterConfig, FilterValues, FilterCallbacks } from "../../../types/common/filters";
import { Stack, ActionList, ActionMenu, Button, IconButton } from '@primer/react';
import { ChevronRightIcon, FilterIcon } from '@primer/octicons-react'
import { useFilterOptions } from "../../../hooks/useFilterOptions";

export interface MetricFilterModalProps {
    filterOpened: boolean;
    setFilterOpened: (value: boolean) => void;
    priceFilterOpened: boolean;
    setPriceFilterOpened: (value: boolean) => void;
    dateFilterOpened: boolean;
    setDateFilterOpened: (value: boolean) => void;
    selectFilterOpened: string | null;
    setSelectFilterOpened: (value: string | null) => void;
    detailedSelectFilterOpened: string | null;
    setDetailedSelectFilterOpened: (value: string | null) => void;
    config: FilterConfig;
    values: FilterValues;
    callbacks: FilterCallbacks;
    onDismiss: () => void;
    onApply: () => void;
}

export const MetricFilterModal: React.FC<MetricFilterModalProps> = ({
    filterOpened,
    setFilterOpened,
    priceFilterOpened,
    setPriceFilterOpened,
    dateFilterOpened,
    setDateFilterOpened,
    selectFilterOpened,
    setSelectFilterOpened,
    detailedSelectFilterOpened,
    setDetailedSelectFilterOpened,
    config,
    values,
    callbacks,
    onDismiss,
    onApply
}) => {
    const {
        priceFilter = { isShow: true, label: "По цене" },
        dateFilter = { isShow: true, label: "По дате размещения", mode: 'full' },
        selectFilters = []
    } = config;

    const { onFilterChange, onPriceChange, onDateChange } = callbacks;

    const {
        loadedOptions,
        loadingStates,
        filterHasMore,
        filterLoadingMore,
        loadFilterOptions,
        handleLoadMore,
        resetFilterData,
        getItems,
        getSelectedLabelForSubmenu
    } = useFilterOptions({ values });

    const [selectedValues, setSelectedValues] = useState<Record<FilterKey, Set<string>>>({} as Record<FilterKey, Set<string>>);

    const selectedCountryName = values['countryResponseForName'];
    const selectedCountryId = values['countryResponseForId'];
    const selectedCategoryId = values['category'];

    useEffect(() => {
        if (selectedCountryId) {
            onFilterChange('cityResponseForId', []);
            setSelectedValues(prev => ({ ...prev, cityResponseForId: new Set([]) }));
            resetFilterData('cityResponseForId');
        }
    }, [selectedCountryId]);

    useEffect(() => {
        if (selectedCountryName) {
            onFilterChange('cityResponseForName', []);
            setSelectedValues(prev => ({ ...prev, cityResponseForName: new Set([]) }));
            resetFilterData('cityResponseForName');
        }
    }, [selectedCountryName]);

    useEffect(() => {
        if (selectedCategoryId) {
            onFilterChange('subcategory', []);
            setSelectedValues(prev => ({ ...prev, subcategory: new Set([]) }));
            resetFilterData('subcategory');
        }
    }, [selectedCategoryId]);

    const fromPrice = values.price?.from || '';
    const toPrice = values.price?.to || '';
    const fromDate = values.date?.from || '';
    const toDate = values.date?.to || '';

    const fromPriceNumber = parseFloat(fromPrice);
    const toPriceNumber = parseFloat(toPrice);

    const priceFormError = fromPrice !== '' && toPrice !== '' && !isNaN(fromPriceNumber) && !isNaN(toPriceNumber) && fromPriceNumber > toPriceNumber;
    const dateFormError = fromDate !== '' && toDate !== '' && new Date(fromDate) > new Date(toDate);

    const detailedFilters: FilterKey[] = ['countryResponseForName', 'cityResponseForName', 'category', 'subcategory', 'filterName'];
    const singleSelectFilters: FilterKey[] = ['hint', 'userType'];

    const getItemValueString = (itemValue: any): string => {
        return typeof itemValue === 'string' ? itemValue : itemValue?.value;
    };

    const handleSelect = (filterKey: FilterKey, value: string) => {
        const currentSelected = selectedValues[filterKey] || new Set<string>([]);
        const newSelectedValues = new Set(currentSelected);

        if (currentSelected.has(value)) {
            newSelectedValues.delete(value);
        } else {
            newSelectedValues.add(value);
        }

        setSelectedValues(prev => ({ ...prev, [filterKey]: newSelectedValues }));

        const items = loadedOptions[filterKey] || [];
        const selectedItems = Array.from(newSelectedValues)
            .map(val => items.find(item => {
                const itemValue = getItemValueString(item.value);
                return itemValue === val;
            }))
            .filter(item => item !== undefined)
            .map(item => item!.value);

        onFilterChange(filterKey, selectedItems);
    };

    const priceModalDidDismiss = () => {
        setPriceFilterOpened(false);
        setFilterOpened(true);
    };

    const dateModalDidDismiss = () => {
        setDateFilterOpened(false);
        setFilterOpened(true);
    };

    const selectDetailedModalDidDismiss = () => {
        setDetailedSelectFilterOpened(null);
        setFilterOpened(true);
    };

    const selectModalDidDismiss = () => {
        setSelectFilterOpened(null);
        setFilterOpened(true);
    };

    const handleDismiss = () => {
        setFilterOpened(false);
        setSelectedValues({} as Record<FilterKey, Set<string>>);
        onDismiss();
    };

    const handleApply = () => {
        setFilterOpened(false);
        onApply();
    };

    const handlePriceChange = (from: string, to: string) => {
        onPriceChange(from, to);
    };

    return (
        <>
            {priceFilter.isShow && (
                <PriceFormModal
                    priceFilterOpened={priceFilterOpened}
                    setPriceFilterOpened={setPriceFilterOpened}
                    fromPrice={fromPrice}
                    setFromPrice={(value) => handlePriceChange(value, toPrice)}
                    toPrice={toPrice}
                    setToPrice={(value) => handlePriceChange(fromPrice, value)}
                    priceFormError={priceFormError}
                    onDismiss={priceModalDidDismiss}
                    onApply={priceModalDidDismiss}
                />
            )}

            {dateFilter.isShow && (
                <DateFormModal
                    dateFilterOpened={dateFilterOpened}
                    setDateFilterOpened={setDateFilterOpened}
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={(value) => onDateChange(value, toDate)}
                    setToDate={(value) => onDateChange(fromDate, value)}
                    dateFormError={dateFormError}
                    onDismiss={dateModalDidDismiss}
                    onApply={dateModalDidDismiss}
                    onClearDates={() => onDateChange('', '')}
                    isShowBackButton={true}
                    mode={dateFilter.mode}
                />
            )}

            {selectFilters.map((selectFilter) => {
                if (!detailedFilters.includes(selectFilter.key)) return null;

                const items = loadedOptions[selectFilter.key] || [];
                const isLoading = loadingStates[selectFilter.key];
                const currentSelectedValues = selectedValues[selectFilter.key] || new Set<string>([]);

                return (
                    <SelectFormModal
                        key={selectFilter.key + '-modal'}
                        selectFilterOpened={detailedSelectFilterOpened === selectFilter.key}
                        setSelectFilterOpened={(isOpen) => setDetailedSelectFilterOpened(isOpen ? selectFilter.key : null)}
                        label={selectFilter.label}
                        items={items}
                        selectedValues={currentSelectedValues}
                        onSelect={(value) => handleSelect(selectFilter.key, value)}
                        onDismiss={selectDetailedModalDidDismiss}
                        isLoading={isLoading}
                        hasMore={filterHasMore[selectFilter.key]}
                        isLoadingMore={filterLoadingMore[selectFilter.key]}
                        onLoadMore={() => handleLoadMore(selectFilter.key)}
                    />
                );
            })}

            {selectFilters.map((selectFilter) => {
                if (detailedFilters.includes(selectFilter.key)) return null;

                return (
                    <ActionMenu
                        key={selectFilter.key}
                        open={selectFilterOpened === selectFilter.key}
                        onOpenChange={(isOpen) => setSelectFilterOpened(isOpen ? selectFilter.key : null)}
                    >
                        <ActionMenu.Overlay className="!w-[260px]">
                            <ActionList
                                selectionVariant="single"
                                role="menu"
                                aria-label={selectFilter.label}
                                className="!w-[260px]"
                            >
                                {selectFilter.options.map((option, index) => (
                                    <ActionList.Item
                                        key={index}
                                        role="menuitemradio"
                                        selected={option.value === values[selectFilter.key]}
                                        aria-checked={option.value === values[selectFilter.key]}
                                        onSelect={() => {
                                            onFilterChange(selectFilter.key, option.value);
                                            selectModalDidDismiss();
                                        }}
                                    >
                                        <Stack align="center" direction="horizontal" gap='none'>
                                            {option.label}
                                        </Stack>
                                    </ActionList.Item>
                                ))}
                            </ActionList>
                        </ActionMenu.Overlay>
                    </ActionMenu>
                );
            })}

            <ActionMenu open={filterOpened} onOpenChange={setFilterOpened}>
                <ActionMenu.Anchor>
                    <IconButton icon={FilterIcon} aria-label="Фильтр" />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay className="!w-[260px]">
                    <ActionList role="menu" aria-label="Item" className="!w-[260px]">
                        <ActionList.Group>
                            {priceFilter.isShow && (
                                <ActionList.Item key='price' onSelect={() => setPriceFilterOpened(true)}>
                                    <p className='font-normal'>{priceFilter.label}</p>
                                    <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                    <ActionList.Description variant="block">
                                        {formatPriceRange(fromPrice, toPrice)}
                                    </ActionList.Description>
                                </ActionList.Item>
                            )}

                            {dateFilter.isShow && (
                                <ActionList.Item key='date' onSelect={() => setDateFilterOpened(true)}>
                                    {dateFilter.icon && <ActionList.LeadingVisual><dateFilter.icon /></ActionList.LeadingVisual>}
                                    <p className='font-normal whitespace-pre-line'>{dateFilter.label}</p>
                                    <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                    <ActionList.Description variant="block">
                                        {formatDateRange(fromDate, toDate, dateFilter.mode)}
                                    </ActionList.Description>
                                </ActionList.Item>
                            )}

                            {selectFilters.map((selectFilter) => {
                                const items = getItems(selectFilter);
                                const isLoading = loadingStates[selectFilter.key];
                                const isDetailedFilter = detailedFilters.includes(selectFilter.key);
                                const isSingleSelect = singleSelectFilters.includes(selectFilter.key);

                                if (isDetailedFilter) {
                                    return (
                                        <ActionList.Item
                                            key={selectFilter.key}
                                            onSelect={() => {
                                                resetFilterData(selectFilter.key);
                                                if ((
                                                    selectFilter.key === 'cityResponseForName' ||
                                                    selectFilter.key === 'cityResponseForId' ||
                                                    selectFilter.key === 'category' ||
                                                    selectFilter.key === 'subcategory'
                                                ) || selectFilter.options.length === 0) {
                                                    loadFilterOptions(selectFilter.key, false, true);
                                                }
                                                setDetailedSelectFilterOpened(selectFilter.key);
                                            }}
                                        >
                                            {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                            <p className='font-normal'>{selectFilter.label}</p>
                                            <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                            <ActionList.Description variant="block">
                                                {getSelectedLabelForSubmenu(selectFilter, selectedValues)}
                                            </ActionList.Description>
                                        </ActionList.Item>
                                    );
                                }

                                return (
                                    <ActionMenu
                                        key={selectFilter.key}
                                        onOpenChange={(open) => {
                                            if (selectFilter.options.length === 0) {
                                                loadFilterOptions(selectFilter.key);
                                            }
                                        }}
                                    >
                                        <ActionMenu.Anchor>
                                            <ActionList.Item>
                                                {selectFilter.icon && <ActionList.LeadingVisual><selectFilter.icon /></ActionList.LeadingVisual>}
                                                <p className='font-normal'>{selectFilter.label}</p>
                                                <ActionList.TrailingVisual><ChevronRightIcon /></ActionList.TrailingVisual>
                                                <ActionList.Description variant="block">
                                                    {isSingleSelect
                                                        ? (items.find(item => item.value === values[selectFilter.key])?.label || '')
                                                        : getSelectedLabelForSubmenu(selectFilter, selectedValues)
                                                    }
                                                </ActionList.Description>
                                            </ActionList.Item>
                                        </ActionMenu.Anchor>
                                        <ActionMenu.Overlay>
                                            <ActionList
                                                selectionVariant={isSingleSelect ? "single" : "multiple"}
                                                role="menu"
                                                aria-label="Item"
                                            >
                                                {isLoading ? (
                                                    <ActionList.Item loading>Загрузка...</ActionList.Item>
                                                ) : items.length === 0 ? (
                                                    <ActionList.Item>Нет данных</ActionList.Item>
                                                ) : (
                                                    items.map((item, index) => {
                                                        const itemValueString = getItemValueString(item.value);
                                                        const isSelected = isSingleSelect
                                                            ? values[selectFilter.key] === item.value
                                                            : selectedValues[selectFilter.key]?.has(itemValueString);

                                                        return (
                                                            <ActionList.Item
                                                                key={item.label + '-submenu'}
                                                                role="menuitemradio"
                                                                selected={isSelected}
                                                                aria-checked={isSelected}
                                                                onSelect={(e) => {
                                                                    e.preventDefault();
                                                                    if (isSingleSelect) {
                                                                        const newValue = values[selectFilter.key] === item.value ? null : item.value;
                                                                        onFilterChange(selectFilter.key, newValue);
                                                                    } else {
                                                                        handleSelect(selectFilter.key, itemValueString);
                                                                    }
                                                                }}
                                                            >
                                                                {item.label}
                                                            </ActionList.Item>
                                                        );
                                                    })
                                                )}
                                            </ActionList>
                                        </ActionMenu.Overlay>
                                    </ActionMenu>
                                );
                            })}
                        </ActionList.Group>

                        <Stack direction="horizontal" gap='normal' className='block mt-4 ml-[66px]'>
                            <Button onClick={handleDismiss} size='small' variant="default">
                                Очистить
                            </Button>
                            <Button onClick={handleApply} size='small' variant="primary">
                                Применить
                            </Button>
                        </Stack>
                    </ActionList>
                </ActionMenu.Overlay>
            </ActionMenu>
        </>
    );
};